import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Group, NumberInput, Select, SimpleGrid, Stack, Text, TextInput } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { AppModal } from '../../shared/components/AppModal';
import { ApiError } from '../../shared/api/problem';
import { listItems, listItemUoms } from '../items/api';
import { savePurchaseOrderLine, type PurchaseOrderLineDto } from './api';

interface Props {
  purchaseOrderId: string;
  /** Null adds a line; a row edits it. */
  line: PurchaseOrderLineDto | null;
  opened: boolean;
  onClose: () => void;
  onSaved: (lines: PurchaseOrderLineDto[]) => void;
}

/**
 * One purchase order line.
 *
 * The unit list comes from the ITEM, not the global one: the server refuses a unit the item has no
 * conversion for (53743), because a receipt in that unit would have nothing to convert with. So the
 * picker only offers units that will actually post.
 *
 * No line number field. It is assigned server-side under a lock, and it is never renumbered when a
 * line is deleted — the supplier is holding a piece of paper quoting line 3.
 */
export function PurchaseOrderLineModal({ purchaseOrderId, line, opened, onClose, onSaved }: Props) {
  const { t } = useTranslation();
  const isEdit = line !== null;

  const [itemId, setItemId] = useState('');
  const [quantity, setQuantity] = useState<number | ''>('');
  const [uomId, setUomId] = useState('');
  const [unitPrice, setUnitPrice] = useState<number | ''>('');
  const [requiredBy, setRequiredBy] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  const items = useQuery({
    queryKey: ['items', 'active'] as const,
    queryFn: ({ signal }) => listItems(false, signal),
    enabled: opened,
  });

  const itemUoms = useQuery({
    queryKey: ['item-uoms', itemId || 'none'] as const,
    queryFn: ({ signal }) => listItemUoms(itemId, signal),
    enabled: opened && Boolean(itemId),
  });

  useEffect(() => {
    if (!opened) return;
    setErrors({});
    setSubmitError(null);
    setItemId(line?.itemId ?? '');
    setQuantity(line?.quantityOrdered ?? '');
    setUomId(line?.uomId ?? '');
    setUnitPrice(line?.unitPrice ?? '');
    setRequiredBy(line?.dateRequiredByUtc ? line.dateRequiredByUtc.slice(0, 10) : '');
  }, [opened, line]);

  const selectedItem = useMemo(
    () => (items.data ?? []).find((i) => i.itemId === itemId) ?? null,
    [items.data, itemId],
  );

  // A new line defaults to the item's stocking unit, which is what gets ordered most of the time.
  // An edit keeps whatever the line already says.
  useEffect(() => {
    if (isEdit || !selectedItem) return;
    setUomId(selectedItem.primaryUomId);
  }, [isEdit, selectedItem]);

  const mutation = useMutation({
    mutationFn: () =>
      savePurchaseOrderLine(purchaseOrderId, line?.purchaseOrderLineId ?? null, {
        itemId,
        quantityOrdered: Number(quantity),
        uomId,
        unitPrice: unitPrice === '' ? 0 : Number(unitPrice),
        dateRequiredByUtc: requiredBy ? `${requiredBy}T00:00:00Z` : null,
      }),
    onSuccess: (lines) => {
      onSaved(lines);
      onClose();
    },
    onError: (err) => {
      if (!(err instanceof ApiError)) {
        setSubmitError(t('purchaseOrders.errors.lineFailed'));
        return;
      }
      // uom_not_convertible names the item and the unit; the reduce-below-received refusal names
      // how much has already arrived. Both are more use than anything restated here.
      setSubmitError(err.problem.detail ?? err.problem.title ?? t('purchaseOrders.errors.lineFailed'));
    },
  });

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError(null);
    const next: Record<string, string> = {};
    if (!itemId) next.itemId = t('purchaseOrders.errors.itemRequired');
    if (!uomId) next.uomId = t('purchaseOrders.errors.uomRequired');
    if (quantity === '' || Number(quantity) <= 0) {
      next.quantity = t('purchaseOrders.errors.quantityPositive');
    }
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    mutation.mutate();
  }

  const lineTotal =
    quantity === '' || unitPrice === '' ? null : Number(quantity) * Number(unitPrice);

  return (
    <AppModal
      opened={opened}
      onClose={onClose}
      title={isEdit ? t('purchaseOrders.editLine', { number: line!.lineNumber }) : t('purchaseOrders.addLine')}
      width="md"
    >
      <form onSubmit={onSubmit} noValidate>
        <Stack gap="md">
          <Select
            label={t('purchaseOrders.item')}
            placeholder={t('purchaseOrders.itemPlaceholder')}
            data={(items.data ?? []).map((i) => ({
              value: i.itemId,
              label: `${i.itemNumber} — ${i.description}`,
            }))}
            value={itemId || null}
            onChange={(v) => setItemId(v ?? '')}
            error={errors.itemId}
            disabled={items.isLoading}
            searchable
            allowDeselect={false}
            required
          />

          {/* Received quantity is not editable here — it comes from receiving (M4.3). Showing it
              explains why a line may refuse to shrink. */}
          {isEdit && line!.quantityReceived > 0 && (
            <Alert color="blue" variant="light" icon={<IconAlertCircle size={16} />}>
              {t('purchaseOrders.lineAlreadyReceived', { quantity: line!.quantityReceived })}
            </Alert>
          )}

          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            <NumberInput
              label={t('purchaseOrders.quantityOrdered')}
              value={quantity}
              onChange={(v) => setQuantity(v === '' ? '' : Number(v))}
              error={errors.quantity}
              min={0}
              decimalScale={4}
              required
            />
            <Select
              label={t('purchaseOrders.unit')}
              description={t('purchaseOrders.unitHint')}
              data={(itemUoms.data ?? []).map((u) => ({
                value: u.uomId,
                label: `${u.uomCode} — ${u.displayName}`,
              }))}
              value={uomId || null}
              onChange={(v) => setUomId(v ?? '')}
              error={errors.uomId}
              disabled={!itemId || itemUoms.isLoading}
              searchable
              allowDeselect={false}
              required
            />
          </SimpleGrid>

          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            <NumberInput
              label={t('purchaseOrders.unitPrice')}
              description={t('purchaseOrders.unitPriceHint')}
              value={unitPrice}
              onChange={(v) => setUnitPrice(v === '' ? '' : Number(v))}
              min={0}
              decimalScale={4}
            />
            <TextInput
              type="date"
              label={t('purchaseOrders.requiredBy')}
              value={requiredBy}
              onChange={(e) => setRequiredBy(e.currentTarget.value)}
            />
          </SimpleGrid>

          {lineTotal !== null && (
            <Text size="sm" c="dimmed">
              {t('purchaseOrders.lineTotalPreview', {
                total: lineTotal.toLocaleString(undefined, { maximumFractionDigits: 4 }),
              })}
            </Text>
          )}

          {submitError && (
            <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
              {submitError}
            </Alert>
          )}

          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={onClose}>
              {t('shell.cancel')}
            </Button>
            <Button type="submit" color="invetrak" loading={mutation.isPending}>
              {t('shell.saveChanges')}
            </Button>
          </Group>
        </Stack>
      </form>
    </AppModal>
  );
}
